import PQueue from "p-queue";
import type { SiteAdapter, AdapterContext } from "../adapters/base.js";
import type { Logger } from "../utils/logger.js";
import type { NovelMetadata, ChapterRef, NovelData } from "../data-model/novel.js";
import type { DownloadResult } from "../data-model/result.js";
import type { ChapterIndex, Url } from "../data-model/primitives.js";
import { EventBus } from "./events.js";
import { Debouncer } from "./debouncer.js";
import { RunLedger } from "./run-ledger.js";
import { parseChapterIndex } from "../data-model/parse.js";
import { fetchMetadataStage } from "../pipeline/fetch-metadata.js";
import { fetchTocStage } from "../pipeline/fetch-toc.js";
import type { FetchTocRange } from "../pipeline/fetch-toc.js";
import { walkToc } from "../pipeline/walk-toc.js";
import { fetchChapterTask } from "../pipeline/fetch-chapters.js";
import { assembleNovelData } from "../pipeline/normalize.js";
import { CancelledError, ChapterFetchError, normalizeToEnvelopeError } from "./errors.js";
import type { KnownDownloadError } from "./errors.js";
import type { ResumeState } from "../storage/resume-state.js";
import { nowIso } from "../storage/resume-state.js";
import { encodeResumeToken } from "../storage/resume-token.js";
import { ResumeStorage } from "../storage/resume-storage.js";

const QUEUE_BACKPRESSURE_RATIO = 4;
const STATE_PERSIST_DEBOUNCE_MS = 500;

export interface DownloadRunArgs {
  readonly adapter: SiteAdapter;
  readonly validated: Url;
  readonly normalizedRange: FetchTocRange | undefined;
  readonly resumeStorage: ResumeStorage | null;
  readonly concurrency: number;
  readonly signal: AbortSignal;
  readonly ctx: AdapterContext;
  readonly bus: EventBus;
  readonly logger: Logger;
}

export class DownloadRun {
  readonly #adapter: SiteAdapter;
  readonly #validated: Url;
  readonly #normalizedRange: FetchTocRange | undefined;
  readonly #resumeStorage: ResumeStorage | null;
  readonly #concurrency: number;
  readonly #signal: AbortSignal;
  readonly #ctx: AdapterContext;
  readonly #bus: EventBus;
  readonly #logger: Logger;

  #state: ResumeState | null = null;
  #ledger: RunLedger = RunLedger.seed(null, []);
  readonly #queue: PQueue;
  readonly #stateDebouncer: Debouncer;

  constructor(args: DownloadRunArgs) {
    this.#adapter = args.adapter;
    this.#validated = args.validated;
    this.#normalizedRange = args.normalizedRange;
    this.#resumeStorage = args.resumeStorage;
    this.#concurrency = args.concurrency;
    this.#signal = args.signal;
    this.#ctx = args.ctx;
    this.#bus = args.bus;
    this.#logger = args.logger;
    this.#queue = new PQueue({ concurrency: this.#concurrency });
    this.#stateDebouncer = new Debouncer(
      (): Promise<void> => this.#persistState(),
      STATE_PERSIST_DEBOUNCE_MS,
      { logger: this.#logger },
    );
  }

  async execute(): Promise<DownloadResult> {
    const initErr = await this.#initResume();
    if (initErr) return initErr;

    const resolved = await this.#resolveMetadata();
    if ("error" in resolved) return resolved.error;
    const meta = resolved.meta;

    const ctxWithMeta: AdapterContext = { ...this.#ctx, novelMetadata: meta };
    const onAbort = (): void => {
      this.#queue.clear();
    };
    this.#signal.addEventListener("abort", onAbort, { once: true });
    try {
      const stage2Error = await this.#runTocStage(ctxWithMeta);
      await this.#drainQueueOrAbort();
      await this.#flushQuietly();
      return this.#finalize(meta, stage2Error);
    } finally {
      this.#signal.removeEventListener("abort", onAbort);
    }
  }

  async #initResume(): Promise<DownloadResult | null> {
    const resumeCfg = this.#resumeStorage;
    if (!resumeCfg) return null;
    try {
      const { state, priorChapters } = await resumeCfg.loadFor(this.#validated, this.#adapter.id);
      this.#state = state;
      this.#ledger = RunLedger.seed(state, priorChapters);
    } catch (err) {
      return { status: "error", error: normalizeToEnvelopeError(err) };
    }
    return null;
  }

  #schedulePersist(): void {
    if (this.#state) this.#stateDebouncer.schedule();
  }

  async #persistState(): Promise<void> {
    const resumeCfg = this.#resumeStorage;
    if (!resumeCfg || !this.#state) return;
    const next: ResumeState = { ...this.#ledger.applyTo(this.#state), updatedAt: nowIso() };
    this.#state = next;
    await resumeCfg.resume.save(resumeCfg.stateFile, next);
  }

  async #resolveMetadata(): Promise<{ readonly meta: NovelMetadata } | { readonly error: DownloadResult }> {
    try {
      let meta: NovelMetadata;
      if (this.#state?.metadata) {
        meta = this.#state.metadata;
      } else {
        meta = await fetchMetadataStage(this.#adapter, this.#validated, this.#ctx, this.#bus);
        if (this.#state) {
          this.#state = { ...this.#state, metadata: meta, updatedAt: nowIso() };
          await this.#persistState();
        }
      }
      return { meta };
    } catch (err) {
      return { error: { status: "error", error: normalizeToEnvelopeError(err) } };
    }
  }

  async #enqueueRef(ref: ChapterRef, ctxWithMeta: AdapterContext): Promise<void> {
    if (this.#signal.aborted) return;
    if (!this.#ledger.markEnqueued(ref.index)) return;
    const backpressureLimit = this.#concurrency * QUEUE_BACKPRESSURE_RATIO;
    while (this.#queue.size >= backpressureLimit) {
      await this.#queue.onSizeLessThan(backpressureLimit);
      if (this.#signal.aborted) throw new CancelledError({ cause: this.#signal.reason });
    }
    void this.#queue.add((): Promise<void> => this.#fetchChapter(ref, ctxWithMeta));
  }

  async #fetchChapter(ref: ChapterRef, ctxWithMeta: AdapterContext): Promise<void> {
    if (this.#signal.aborted) return;
    try {
      const chapter = await fetchChapterTask(this.#adapter, ref, ctxWithMeta, this.#bus);
      if (this.#signal.aborted) return;
      // Persist before recording in-memory success — if the chapter store
      // throws (ENOSPC, EACCES, atomic-rename loss), the catch below records
      // a clean failure instead of a chapter that's listed in both
      // successes and failures.
      if (this.#resumeStorage) await this.#resumeStorage.chapters.save(chapter);
      this.#ledger.recordSuccess(chapter);
      this.#schedulePersist();
      this.#bus.emit({ type: "chapter:success", chapter });
      this.#bus.emit({
        type: "progress",
        completed: this.#ledger.completedCount,
        total: ctxWithMeta.novelMetadata?.totalChapters ?? this.#ledger.enqueuedCount,
      });
    } catch (cause) {
      if (cause instanceof CancelledError) return;
      const wrapped =
        cause instanceof ChapterFetchError ? cause : new ChapterFetchError(ref, { cause });
      this.#ledger.recordFailure(ref, wrapped);
      this.#schedulePersist();
      this.#bus.emit({ type: "chapter:failed", ref, error: wrapped });
    }
  }

  async #runTocStage(ctxWithMeta: AdapterContext): Promise<Exclude<KnownDownloadError, CancelledError> | undefined> {
    let stage2Error: Exclude<KnownDownloadError, CancelledError> | undefined;
    try {
      if (this.#state?.toc) {
        const toc = this.#state.toc;
        async function* fromState(): AsyncIterable<ChapterRef> {
          for (const ref of toc) yield ref;
        }
        await walkToc(fromState(), {
          ...(this.#normalizedRange !== undefined ? { range: this.#normalizedRange } : {}),
          bus: this.#bus,
          signal: this.#signal,
          onRef: (ref) => this.#enqueueRef(ref, ctxWithMeta),
        });
      } else {
        const tocBuffer: ChapterRef[] = [];
        await fetchTocStage(
          this.#adapter,
          this.#validated,
          ctxWithMeta,
          this.#bus,
          async (ref) => {
            tocBuffer.push(ref);
            await this.#enqueueRef(ref, ctxWithMeta);
          },
          this.#normalizedRange,
        );
        if (this.#state) {
          this.#state = { ...this.#state, toc: tocBuffer };
          await this.#persistState();
        }
      }
      await this.#retryFailedRecords(ctxWithMeta);
    } catch (err) {
      if (err instanceof CancelledError) {
        this.#queue.clear();
        await this.#stateDebouncer.cancel();
        throw err;
      }
      stage2Error = normalizeToEnvelopeError(err);
    }
    return stage2Error;
  }

  async #retryFailedRecords(ctxWithMeta: AdapterContext): Promise<void> {
    if (!this.#state) return;
    // Build an index-keyed lookup so the failed-records retry loop is O(N+F)
    // instead of O(N*F) — significant for large novels with many failures.
    const tocByIndex = new Map<ChapterIndex, ChapterRef>(
      (this.#state.toc ?? []).map((r) => [r.index, r]),
    );
    for (const f of this.#ledger.failedRecords()) {
      const ref = tocByIndex.get(f.index);
      if (ref) {
        await this.#enqueueRef(ref, ctxWithMeta);
      } else {
        // Failed record points to an index not present in the current TOC
        // (e.g. the source novel removed a chapter between runs). Drop it
        // from state.failed so it doesn't linger forever.
        this.#logger.warn(
          `Resume: dropping orphan failed record for index ${String(f.index)} (no matching TOC ref)`,
        );
        this.#ledger.dropOrphanFailure(f.index);
        this.#schedulePersist();
      }
    }
  }

  async #drainQueueOrAbort(): Promise<void> {
    let onAbortResolve: (() => void) | undefined;
    const abortPromise = new Promise<void>((resolve) => {
      if (this.#signal.aborted) {
        resolve();
        return;
      }
      onAbortResolve = (): void => resolve();
      this.#signal.addEventListener("abort", onAbortResolve, { once: true });
    });
    await Promise.race([this.#queue.onIdle(), abortPromise]);
    if (onAbortResolve) this.#signal.removeEventListener("abort", onAbortResolve);

    if (this.#signal.aborted) {
      await this.#stateDebouncer.cancel();
      throw new CancelledError({ cause: this.#signal.reason });
    }
  }

  async #flushQuietly(): Promise<void> {
    try {
      await this.#stateDebouncer.flush();
    } catch (err) {
      // Resume-state persistence is best-effort: the chapters are already in
      // `data` and on disk, and state self-heals on the next resume via
      // chapter-file reconciliation. Never sink a finished download over it.
      this.#logger.warn({ err, url: this.#validated }, "resume state persist failed (non-fatal)");
    }
  }

  #finalize(
    metadata: NovelMetadata,
    stage2Error: Exclude<KnownDownloadError, CancelledError> | undefined,
  ): DownloadResult {
    if (stage2Error) return { status: "error", error: stage2Error };

    let data: NovelData;
    try {
      data = assembleNovelData(metadata, this.#ledger.successes());
    } catch (err) {
      return {
        status: "error",
        error: normalizeToEnvelopeError(err),
      };
    }

    const failures = this.#ledger.failures();
    if (failures.length > 0) {
      if (this.#resumeStorage) {
        const token = encodeResumeToken(this.#resumeStorage.stateFile, this.#validated);
        return { status: "partial", resumable: true, data, failures, resumeToken: token };
      }
      return { status: "partial", resumable: false, data, failures };
    }
    return { status: "success", data };
  }
}

export function normalizeChapterRange(
  range: { from?: number; to?: number } | undefined,
): FetchTocRange | undefined {
  if (range === undefined) return undefined;
  const out: { from?: ChapterIndex; to?: ChapterIndex } = {};
  if (range.from !== undefined) out.from = parseChapterIndex(range.from);
  if (range.to !== undefined) out.to = parseChapterIndex(range.to);
  return out;
}
